import React, { FormEventHandler, useContext, useEffect, useState } from 'react';
import { useI18nContext } from '@ecg-internal/js-react-i18n';
import { Form, PrimaryButton, TertiaryButton } from '@hz-design-system/web-ui';

import CarsSearchFormDropDown from './CarsSearchFormDropDown';
import CarsSearchLeaseTypeToggle from './CarsSearchLeaseTypeToggle';
import CarsContinueSearch from './CarsContinueSearch';
import Classes from './CarsSimpleSearch.module.scss';

import { TCarsSearchFieldsClient, TFormState } from 'types/TCarsSearchFields';
import EnvironmentContext from '../../../contexts/EnvironmentContext';

type TCarsSimpleSearchProps = {
  formFields: TCarsSearchFieldsClient;
  resultCount?: number;
  onSubmit: (e?: React.MouseEvent<HTMLElement>) => void;
  onChange: Function;
  toggleAdvancedSearch: Function;
  formState: TFormState;
  simpleSearchKeys: string[];
  lastSearch?: TSearchEntry;
  onRemoveLastSearch: Function;
};

const TRANSITION_DELAY = 100;

const CarsSimpleSearch = ({
  formFields,
  resultCount,
  onSubmit,
  onChange,
  toggleAdvancedSearch,
  formState,
  simpleSearchKeys,
  lastSearch,
  onRemoveLastSearch,
}: TCarsSimpleSearchProps) => {
  const { t } = useI18nContext();
  const {
    links: { carsSearchBaseUrl },
  } = useContext(EnvironmentContext);
  const [transitionClass, setTransitionClass] = useState('');
  const [countLabel, setCountLabel] = useState(resultCount);

  useEffect(() => {
    setTransitionClass('start');
    setTimeout(() => {
      setCountLabel(resultCount);
      setTransitionClass('end');
    }, TRANSITION_DELAY);
  }, [resultCount]);

  const renderSearchComponent = (fieldKey: string) => {
    if (fieldKey === 'l2CategoryId') {
      return (
        <CarsSearchFormDropDown
          key="l2CategoryId"
          fieldKey="l2CategoryId"
          formField={formFields['l2CategoryId']}
          value={formState['l2CategoryId']}
          onChangeHandler={onChange}
          fixedField
        />
      );
    }

    if (['brand', 'model'].includes(fieldKey)) {
      return (
        <CarsSearchFormDropDown
          key={fieldKey}
          fieldKey={fieldKey}
          formField={formFields[fieldKey]}
          value={formState['attributes']?.[fieldKey]?.[0]}
          disabled={!formFields[fieldKey]} // Only for Merk: if formfields.brand has options and none selected
          onChangeHandler={onChange}
          fixedField
        />
      );
    }

    if (['transmission', 'fuel'].includes(fieldKey)) {
      return (
        <CarsSearchFormDropDown
          key={fieldKey}
          fieldKey={fieldKey}
          formField={formFields[fieldKey]}
          value={formState['attributes']?.[fieldKey]?.[0]}
          onChangeHandler={onChange}
          fixedField
        />
      );
    }

    if (fieldKey === 'leaseTypeGroups') {
      return (
        <CarsSearchLeaseTypeToggle
          key="leaseTypeGroups"
          values={formState['attributes']?.['leaseType']}
          onChangeHandler={onChange}
        />
      );
    }

    if (fieldKey === 'PriceCents') {
      return (
        <CarsSearchFormDropDown
          key={`${fieldKey}.to`}
          fieldKey={`${fieldKey}.to`}
          formField={formFields[fieldKey]}
          value={formState['rangeAttributes']?.[fieldKey]?.to}
          onChangeHandler={onChange}
          optionalText={t('cars.search.fields.rangeMaximum')}
          fixedField
        />
      );
    }

    if (fieldKey === 'batteryChargeTime') {
      return (
        <CarsSearchFormDropDown
          key={`${fieldKey}.to`}
          fieldKey={`${fieldKey}.to`}
          formField={formFields[fieldKey]}
          options={formFields[fieldKey]?.optionsTo}
          value={formState['rangeAttributes']?.[fieldKey]?.to}
          onChangeHandler={onChange}
          optionalText={t('cars.search.fields.rangeMaximum')}
          fixedField
        />
      );
    }

    if (['constructionYear', 'range', 'batteryCapacity'].includes(fieldKey)) {
      return (
        <CarsSearchFormDropDown
          key={`${fieldKey}.from`}
          fieldKey={`${fieldKey}.from`}
          formField={formFields[fieldKey]}
          options={formFields[fieldKey]?.optionsFrom}
          value={formState['rangeAttributes']?.[fieldKey]?.from}
          onChangeHandler={onChange}
          optionalText={t('cars.search.fields.rangeFromUpward')}
          fixedField
        />
      );
    }
  };

  return (
    <Form onSubmit={onSubmit as FormEventHandler} action={carsSearchBaseUrl} method="get" className={Classes.root}>
      {lastSearch && <CarsContinueSearch searchEntry={lastSearch} onRemoveLastSearch={onRemoveLastSearch} />}
      <div className={`SimpleFormFields ${Classes.SimpleFormFields}`}>
        {simpleSearchKeys.map((fieldKey) => renderSearchComponent(fieldKey))}
      </div>

      <div className={Classes.FormButtons}>
        <div className={Classes.OtherButtons}>
          <TertiaryButton
            className={`${Classes.FormButton} AdvancedSearchLink`}
            onClick={(e) => toggleAdvancedSearch(e)}
          >
            {t('cars.search.actions.advancedSearch')}
          </TertiaryButton>
        </div>
        <div className={Classes.SubmitButtonContainer}>
          <PrimaryButton
            className={Classes.SubmitButton}
            type="submit"
            aria-label={`${t('cars.search.actions.viewAll')} ${countLabel}${t('cars.search.actions.submitSearchWithCount')}`}
          >
            <span className={`countLabel ${transitionClass}`}>{countLabel}</span>
            <span>{t('cars.search.actions.submitSearchWithCount')}</span>
          </PrimaryButton>
        </div>
      </div>
    </Form>
  );
};

export default CarsSimpleSearch;
